import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Trophy } from "lucide-react";

export default function ArenaView({ ch, setCh }) {
  const { t } = useTranslation();
  const [opponents, setOpponents] = useState([]);
  const [history, setHistory] = useState([]);
  const [wager, setWager] = useState(20);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setOpponents(await api.arenaOpponents());
    setHistory(await api.arenaHistory());
  };
  useEffect(() => { refresh(); }, []);

  const duel = async (target_id = null) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.arenaDuel(wager, target_id);
      setCh(res.character);
      await refresh();
      if (res.won) toast.success(t("arena.victoryOver", { name: res.opponent, gold: res.payout - wager }));
      else toast.error(t("arena.defeatedBy", { name: res.opponent, gold: wager }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div className="panel p-3 space-y-2" data-testid="arena-form">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
          <Swords className="w-4 h-4 text-[#8A0303]" strokeWidth={1.5} /> {t("arena.ring")}
        </div>
        <p className="italic text-[10px] text-[#666]">{t("arena.rules")}</p>
        <div className="flex gap-2">
          <input
            data-testid="arena-wager"
            type="number"
            min={5}
            value={wager}
            onChange={(e) => setWager(parseInt(e.target.value) || 5)}
            className="flex-1 bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
          />
          <button
            data-testid="arena-random"
            onClick={() => duel(null)}
            disabled={busy || ch.gold < wager || ch.explore_points < 2}
            className="px-4 py-2 border-2 border-[#8A0303] text-[#E33333] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
          >
            {t("arena.random")}
          </button>
        </div>
      </div>

      <div className="panel p-2" data-testid="arena-opponents">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1">
          {t("arena.contenders")} ({opponents.length})
        </div>
        {opponents.length === 0 && (
          <p className="italic text-[#666] text-xs px-2 py-2">{t("arena.noOpponents")}</p>
        )}
        {opponents.map(o => (
          <div key={o.user_id} className="panel-inner p-2 mb-1 flex justify-between items-center" data-testid={`arena-op-${o.user_id}`}>
            <div>
              <div className="font-heading text-sm text-[#E2DFD8]">{o.name}</div>
              <div className="font-mono text-[10px] text-[#8c8c8c]">Lv {o.level} · {o.class}</div>
            </div>
            <button
              data-testid={`arena-duel-${o.user_id}`}
              onClick={() => duel(o.user_id)}
              disabled={busy || ch.gold < wager || ch.explore_points < 2}
              className="px-2 py-1 border border-[#8A0303] text-[#E33333] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
            >
              {t("arena.duel")} ({wager}g)
            </button>
          </div>
        ))}
      </div>

      <div className="panel p-2" data-testid="arena-history">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1 flex items-center gap-2">
          <Trophy className="w-3 h-3 text-[#B89C3A]" strokeWidth={1.5} /> {t("arena.record")}
        </div>
        {history.length === 0 && (
          <p className="italic text-[#666] text-xs px-2 py-2">{t("arena.noHistory")}</p>
        )}
        {history.slice(0, 10).map(h => (
          <div key={h.match_id} className="flex justify-between items-center border-b border-[#1a1a1a] p-1 text-xs">
            <span className={h.won ? "text-[#6E8B3D]" : "text-[#8A0303]"}>
              {h.won ? t("arena.win") : t("arena.loss")} vs {h.opponent_name}
            </span>
            <span className="font-mono text-[10px] text-[#8c8c8c]">
              {h.duel.a_wins}-{h.duel.b_wins} · {h.wager}g
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
